<template>
  <custom-popup
    v-model="internalValue"
    :cons="{ title: 'مدیریت تنوع‌های محصول (قیمت‌گذاری چندگانه)' }"
    max-width="1000"
  >
    <v-card flat v-if="product">
      <!-- هدر مشخصات محصول -->
      <div class="pa-4 bg-grey-lighten-4 d-flex align-center border-b">
        <v-img :src="product.image_src" max-width="50" class="rounded mr-4" />
        <div>
          <div class="text-subtitle-1 font-weight-bold">{{ product.title }}</div>
          <div class="caption grey--text">شناسه محصول: {{ product.product_id }}</div>
        </div>
      </div>

      <div v-if="loading" class="d-flex justify-center pa-10">
        <v-progress-circular indeterminate color="primary" />
      </div>

      <div v-else-if="variants.length > 0">
        <custom-tabs v-model="activeTab" :tab-items="tabItems">
          <template v-slot:staticItems>
            <v-tabs-items v-model="activeTab">
              <v-tab-item v-for="(variant, index) in variants" :key="index">
                
                <v-card flat class="pa-6">
                  <!-- نوار اطلاعات تنوع و رنگ -->
                  <div class="d-flex align-center mb-4 pa-3 rounded-lg border bg-light">
                    <span 
                      class="color-box rounded mr-2" 
                      :style="{ backgroundColor: getColorHex(variant.color) }"
                    ></span>
                    <span class="font-weight-bold text-body-2 primary--text mr-2">
                      {{ variant.color || 'رنگ مشخص نشده' }}
                    </span>
                    <span class="grey--text text--lighten-1 mr-2">|</span>
                    <span class="text-subtitle-2 grey--text text--darken-2">
                      {{ variant.title }}
                    </span>
                  </div>

                  <v-form :ref="'form' + index" lazy-validation>
                    
                    <!-- رندر کردن داینامیک رول‌ها -->
                    <v-expansion-panels v-model="activePanel" class="mb-6" accordion>
                      <v-expansion-panel 
                        v-for="(rule, rIndex) in formData.rules" 
                        :key="rIndex"
                        class="elevation-2 rounded-lg border-primary mb-2"
                      >
                        <v-expansion-panel-header class="bg-grey-lighten-4 py-2 px-4">
                          <template v-slot:default>
                            <div class="d-flex justify-space-between align-center w-100 pl-4">
                              <span class="text-subtitle-2 font-weight-bold">حالت {{ rIndex + 1 }}</span>
                              <v-btn v-if="formData.rules.length > 1" icon color="error" small @click.stop="removeRule(rIndex)">
                                <v-icon>mdi-trash-can-outline</v-icon>
                              </v-btn>
                            </div>
                          </template>
                        </v-expansion-panel-header>
                        
                        <v-expansion-panel-content class="pt-4">
                          <v-row class="mb-2 align-center">
                            <v-col cols="12" class="d-flex flex-column py-0">
                              <div class="text-caption text-grey mb-1">روش ارسال</div>
                              <div class="d-flex align-center gap-4 bg-blue-grey lighten-5 pa-2 rounded-lg">
                                <v-checkbox v-model="rule.is_fbd_active" label="دیجی‌کالا" dense hide-details class="mt-0" />
                                <v-checkbox v-model="rule.is_fbs_active" label="فروشنده" dense hide-details class="mt-0" />
                                <v-checkbox v-model="rule.is_three_hour_delivery_active" label="۳ ساعته" dense hide-details class="mt-0" />
                              </div>
                            </v-col>
                          </v-row>
                          
                          <v-row>
                            <v-col cols="12" md="6">
                              <price-input 
                                v-model="rule.min_price" 
                                label="حداقل قیمت نهایی" 
                                dense 
                                :rules="[(v) => !!v || 'الزامی است']" 
                                :min="0"
                              />
                            </v-col>
                            <v-col cols="12" md="6">
                              <price-input 
                                v-model="rule.max_price" 
                                label="حداکثر قیمت نهایی" 
                                dense
                                :rules="[ 
                                  (v) => !!v || 'الزامی است',
                                  (v) => Number(String(v).replace(/,/g, '')) > Number(String(rule.min_price || 0).replace(/,/g, '')) || 'باید بزرگتر از حداقل قیمت باشد'
                                ]"
                              />
                            </v-col>
                            <v-col cols="12" md="6">
                              <price-input 
                                v-model.number="rule.price_step" 
                                label="کسر قیمت"
                                :rules="[(v) => !!v || 'الزامی است']"
                                min="2000" dense
                              />
                            </v-col>
                            <v-col cols="12" md="6" class="d-flex align-center gap-4">
                              <v-switch v-model="rule.has_discount" :true-value="1" :false-value="0" :label="rule.has_discount===1 ? 'تخفیف' : 'کسر قیمت'" color="primary" hide-details></v-switch>
                            </v-col>
                          </v-row>
                        </v-expansion-panel-content>
                      </v-expansion-panel>
                    </v-expansion-panels>
                  
                    <div class="d-flex justify-center mb-6">
                      <v-btn class="btn-get-code rounded-lg" @click="addRule">
                        <v-icon left>mdi-plus</v-icon>
                        افزودن حالت قیمت‌گذاری جدید
                      </v-btn>
                    </div>

                    <v-divider class="mb-4"></v-divider>

                    <v-row class="align-center">
                      <v-col cols="12" md="6">
                        <v-switch v-model="formData.is_enabled" label="وضعیت فعالیت کلی برای این تنوع" color="success" hide-details />
                      </v-col>
                      <v-col cols="12" md="6" class="d-flex justify-end gap-2">
                        <v-btn class="btn-get-code rounded-lg px-6" :loading="saving" @click="saveVariantSettings(index)">
                          <v-icon left>mdi-content-save-cog</v-icon>
                          {{ `ذخیره تنوع ${tabItems[activeTab] ? tabItems[activeTab].variant_id : ''}` }}
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-form>

                </v-card>
              </v-tab-item>
            </v-tabs-items>
          </template>
        </custom-tabs>
      </div>

      <v-alert v-else-if="product.variants_count === 0 && variants.length === 0" type="info" class="ma-4">
        هیچ تنوعی برای این محصول یافت نشد.
      </v-alert>
      <v-alert v-else type="info" class="ma-4">
        هیچ تنوع فعالی برای این محصول یافت نشد.
      </v-alert>

    </v-card>
  </custom-popup>
</template>

<script>
import PriceInput from "../../../core/plugins/TitleRibbon/pages/pickers/priceInput.vue";

export default {
  name: "ProductVariantsModal",
  components: { PriceInput },
  props: {
    value: Boolean,
    product: Object
  },
  data: () => ({
    activeTab: 0,
    activePanel: 0,
    loading: false,
    saving: false,
    variants: [],
    formData: {
      id: null, 
      variant_id: null, 
      product_id: null,
      is_enabled: true,
      rules: []
    },
colorMap: {
      "مشکی": "#212121",
      "سیاه": "#212121",
      "سفید": "#FFFFFF",
      "مسی": "#B87333",
      "طلایی": "#FFD700",
      "نقره‌ای": "#C0C0C0",
      "رزگلد": "#B76E79",
      "قرمز": "#F44336",
      "زرشکی": "#800020",
      "آبی": "#2196F3",
      "آبی روشن": "#03A9F4",
      "آبی تیره": "#0D47A1",
      "سرمه‌ای": "#1A237E",
      "فیروزه‌ای": "#00BCD4",
      "سبز": "#4CAF50",
      "سبز روشن": "#8BC34A",
      "سبز تیره": "#1B5E20",
      "زیتونی": "#808000",
      "زرد": "#FFEB3B",
      "خردلی": "#E6C200",
      "نارنجی": "#FF9800",
      "صورتی": "#E91E63",
      "بنفش": "#9C27B0",
      "یاسی": "#C8A2C8",
      "خاکستری": "#9E9E9E",
      "طوسی": "#9E9E9E",
      "قهوه‌ای": "#795548",
      "کرم": "#FFFDD0",
      "بژ": "#F5F5DC",
      "شیری": "#FDF5E6",
      "شفاف": "#E0E0E0",
      "بی‌رنگ": "#E0E0E0",
      "چندرنگ": "#9C27B0",
      "آبی فیروزه ای": "#00CED1",
       "سبز فسفری": "#7FFF00", "یشمی": "#006400", "سبز آبی": "#008080", "سفید صدفی": "#FFF5EE", "نقره ای آبی": "#778899", "بی رنگ مات": "#F0F0F0", "بی رنگ شفاف": "#FAFAFA", "ماهگونی": "#C04000", "بنفش تیره": "#4B0082", "سرخابی": "#FF00FF", "صورتی تیره": "#E75480", "قهوه ای تیره": "#654321", "قهوه ای روشن": "#D2B48C", "زرد کمرنگ": "#FFFFE0", "نارنجی کمرنگ": "#FFDAB9", "آفتابی": "#FDB813", "مهتابی": "#F4F6F0", "سفید یخی": "#F0F8FF", "سفید چرمی": "#FFF8DC", "صورتی خاکستری": "#C8A2C8", "برنز": "#CD7F32", "تیتانیوم": "#878681", "آبی متالیک": "#32527B", "کرم مات": "#E3DAC9", "سرمه ای تیره": "#000080", "سبز کله غازی": "#008080", "نقره ای مات": "#A9A9A9", "آجری": "#B22222", "زرد لیمویی": "#FFFACD", "مرجانی": "#FF7F50", "آبی کدر": "#5F9EA0", "دودی": "#708090", "پلاتینیوم": "#E5E4E2", "کروم": "#D8D8D8", "قرمز شرابی": "#722F37", "سبز کاهویی": "#8F9779", "مارون": "#800000", "یاقوتی": "#E0115F", "آلومینیومی": "#848482", "طلایی براق": "#FFD700", "جگری": "#6C2E1F", "لاجوردی": "#003399", "کهربایی": "#FFBF00", "بادمجانی": "#311432", "آبی کاربنی": "#0000CD", "طوسی تیره": "#696969", "هلویی": "#FFE5B4", "آلبالویی": "#7B1113", "وانیلی": "#F3E5AB", "آبی آسمانی": "#87CEEB", "نارنجی فسفری": "#FF5E00", "گندمی": "#F5DEB3", "کرم روشن": "#FFFDD0", "بلوند روشن": "#FAF0BE", "قهوه ای روشن براق": "#B5651D", "قرمز تیره": "#8B0000", "سبز فلورسنت": "#00FF00", "نارنجی فلورسنت": "#FF3300", "فندقی": "#AE7037", "آبی کمرنگ": "#ADD8E6", "ارتشی خاکی": "#867E36", "سفید براق": "#FFFFFF", "استیل براق": "#B0C4DE", "ارتشی زیتونی": "#556B2F", "سفید متالیک": "#F8F8FF", "استیل مات": "#778899", "ماسه صحرایی": "#F4A460", "تیتانیوم صحرایی": "#9D8271"
    }
  }),
  computed: {
    internalValue: {
      get() { return this.value; },
      set(v) { this.$emit('input', v); }
    },
    tabItems() {
      return this.variants.map(v => ({
        text: `تنوع: ${v.variant_id}`,
        subText: `به روزرسانی: ${this.jalalian(v.updated_at, "datetime")}`,
        icon: 'tag',
        color: this.getColorHex(v.color), 
        variant_id: v.variant_id,
        colorName: v.color
      }));
    }
  },
  watch: {
    value(val) {
      if (val && this.product) {
        this.fetchVariants();
      }
    },
    activeTab(newIndex) {
      if (this.variants[newIndex]) {
        this.getMultiPricing(this.variants[newIndex].id);
      }
    }
  },
  methods: {
    getColorHex(colorName) {
      if (!colorName) return '#D6D6D6';
      const cleanName = colorName.trim();
      return this.colorMap[cleanName] || '#D6D6D6';
    },
    getEmptyRule() {
      return {
        min_price: 0,
        max_price: 0,
        price_step: 20000,
        has_discount: 0,
        is_fbd_active: true,
        is_fbs_active: true,
        is_three_hour_delivery_active: true,
        lead_time: 1,
        delivery_time: 0,
        seller_lead_time: 3,
      };
    },
    addRule() {
      this.formData.rules.push(this.getEmptyRule());
      this.$nextTick(() => {
        this.activePanel = this.formData.rules.length - 1;
      });
    },
    removeRule(index) {
      this.formData.rules.splice(index, 1);
      if (this.activePanel >= this.formData.rules.length) {
        this.activePanel = Math.max(0, this.formData.rules.length - 1);
      }
    },
    async fetchVariants() {
      this.loading = true;
      try {
        const res = await this.$api.get(`/v1/digikala/products/${this.product.product_id}/variants`);
        this.variants = res.data.data.variants;
        if (this.variants.length > 0) {
          this.activeTab = 0;
          this.getMultiPricing(this.variants[0].id);
        }
      } catch (e) {
        this.$toast.error("خطا در دریافت تنوع‌ها");
      } finally {
        this.loading = false;
      }
    },
    async getMultiPricing(vId) {
      this.activePanel = 0;
      try {
        const res = await this.$api.get(`/v1/digikala/multi-pricing/find/${vId}`);
        const data = res?.data?.data;
        if (data && data.rules && data.rules.length > 0) {
          this.formData = {
            id: data.id,
            variant_id: vId,
            product_id: this.product.product_id,
            is_enabled: data.is_enabled,
            rules: data.rules
          };
        } else {
          this.resetForm(vId);
        }
      } catch (e) {
        this.resetForm(vId);
      }
    },
    resetForm(vId) {
      this.formData = {
        id: null, 
        variant_id: vId, 
        product_id: this.product.product_id,
        is_enabled: true,
        rules: [this.getEmptyRule()]
      };
    },
    validateDuplicates() {
      const parsedRules = this.formData.rules.map(rule => ({
        min: Number(String(rule.min_price || 0).replace(/,/g, '')),
        max: Number(String(rule.max_price || 0).replace(/,/g, '')),
        fbd: Boolean(rule.is_fbd_active),
        fbs: Boolean(rule.is_fbs_active),
        three_hour: Boolean(rule.is_three_hour_delivery_active)
      }));

      for (let i = 0; i < parsedRules.length; i++) {
        for (let j = i + 1; j < parsedRules.length; j++) {
          const rule1 = parsedRules[i];
          const rule2 = parsedRules[j];

          const isSameShipping = rule1.fbd === rule2.fbd && 
                                 rule1.fbs === rule2.fbs && 
                                 rule1.three_hour === rule2.three_hour;

          const isSamePrice = rule1.min === rule2.min && rule1.max === rule2.max;

          if (isSameShipping) {
            this.$toast.error(`ترکیب روش‌های ارسال در حالت ${i + 1} با حالت ${j + 1} تکراری است.`);                
            this.activePanel = i; 
            return false;
          }
          if (isSamePrice) {
            this.$toast.error(`اعداد بازه قیمتی در حالت ${i + 1} با حالت ${j + 1} تکراری است.`);                
            this.activePanel = i; 
            return false;
          }
        }
      }
      return true;
    },
    async saveVariantSettings(formIndex) {
      const formRef = this.$refs['form' + formIndex][0] || this.$refs['form' + formIndex];
      if (formRef && !formRef.validate()) return;
      if (!this.validateDuplicates()) return; 

      this.saving = true;
      const payload = {
        product_id: this.formData.product_id,
        variant_id: this.formData.variant_id,
        is_enabled: this.formData.is_enabled,
        rules: this.formData.rules.map(rule => ({
          ...rule,
          min_price: Number(String(rule.min_price).replace(/,/g, '')),
          max_price: Number(String(rule.max_price).replace(/,/g, '')),
        }))
      };

      const url = this.formData.id ? `/v1/digikala/multi-pricing/${this.formData.id}` : "/v1/digikala/multi-pricing/store";
      const method = this.formData.id ? "put" : "post";
      
      try {
        await this.$api[method](url, payload);
        this.$toast.success("تنظیمات با موفقیت ذخیره شد.");
      } catch (e) {
        this.$toast.error("خطا در ذخیره اطلاعات.");
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style scoped>
.gap-4 { gap: 16px; }
.gap-2 { gap: 8px; }

.color-box {
  width: 18px;
  height: 18px;
  display: inline-block;
  border: 1px solid rgba(0, 0, 0, 0.2);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  flex-shrink: 0;
}

.bg-light {
  background-color: #f8f9fa;
}
</style>
